<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useDataTransfer } from 'dashboard/composables/useDataTransfer';
import { useDealsStore } from 'dashboard/stores/deals';

import DealHeader from './DealsHeader/DealHeader.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import DealImportStatusBanner from './DealImportStatusBanner.vue';
import DealImportDialog from './DealsForm/DealImportDialog.vue';
import DealExportDialog from './DealsForm/DealExportDialog.vue';
import DataTransferImportDialog from 'dashboard/components-next/DataTransfer/DataTransferImportDialog.vue';

defineProps({
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, default: '' },
  currentPage: { type: Number, default: 1 },
  totalItems: { type: Number, default: 0 },
  showPaginationFooter: { type: Boolean, default: true },
  viewMode: { type: String, default: 'list' },
  showViewToggle: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
});

/* eslint-disable vue/custom-event-name-casing */
const emit = defineEmits([
  'update:currentPage',
  'search',
  'create',
  'managePipeline',
  'toggleView',
  'filter',
  'select',
  'manage-fields',
  'refresh',
]);
/* eslint-enable vue/custom-event-name-casing */

const { t } = useI18n();
const dealsStore = useDealsStore();
const { isUnified, exportAll } = useDataTransfer();

// Importar/Exportar dados (spec 146) fica centralizado aqui, não em cada página que usa este
// layout — duplicar essa wiring por página foi a causa raiz do bug em DealsPipelineIndex.vue (D17).
const importStatusBannerRef = ref(null);
const importDialogRef = ref(null);
const exportDialogRef = ref(null);
const dataTransferDialogRef = ref(null);

const startImportPolling = () => {
  importStatusBannerRef.value?.startPolling();
};

const openImportDialog = () =>
  isUnified.value
    ? dataTransferDialogRef.value?.open()
    : importDialogRef.value?.dialogRef.open();
const openExportDialog = () =>
  isUnified.value ? exportAll() : exportDialogRef.value?.dialogRef.open();

const onImport = async ({ file, fieldMapping }) => {
  try {
    await dealsStore.import({ file, fieldMapping });
    importDialogRef.value?.onSuccess();
    startImportPolling();
    useAlert(t('DEALS.IMPORT.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message ?? t('DEALS.IMPORT.ERROR_MESSAGE'));
  }
};

const onExport = async () => {
  try {
    await dealsStore.export();
    useAlert(t('DEALS.EXPORT.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message ?? t('DEALS.EXPORT.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <section
    class="flex w-full h-full gap-4 overflow-hidden justify-evenly bg-n-surface-1"
  >
    <div class="flex flex-col w-full h-full transition-all duration-300">
      <DealHeader
        :search-value="searchValue"
        :header-title="headerTitle"
        :view-mode="viewMode"
        :show-view-toggle="showViewToggle"
        :has-active-filters="hasActiveFilters"
        @search="emit('search', $event)"
        @create="emit('create')"
        @manage-pipeline="emit('managePipeline')"
        @toggle-view="emit('toggleView')"
        @filter="emit('filter')"
        @import="openImportDialog"
        @export="openExportDialog"
        @select="emit('select')"
        @manage-fields="emit('manage-fields')"
      />
      <DealImportStatusBanner ref="importStatusBannerRef" class="mx-6 mt-2" />
      <main
        class="flex-1 overflow-hidden flex flex-col"
        :class="viewMode === 'list' ? 'overflow-y-auto' : ''"
      >
        <div
          v-if="viewMode === 'list'"
          class="w-full mx-auto max-w-5xl px-6 py-4"
        >
          <slot />
        </div>
        <slot v-else />
      </main>
      <footer v-if="showPaginationFooter" class="sticky bottom-0 z-0">
        <PaginationFooter
          current-page-info="DEALS_LAYOUT.PAGINATION_FOOTER.SHOWING"
          :current-page="currentPage"
          :total-items="totalItems"
          :items-per-page="25"
          class="max-w-[67rem]"
          @update:current-page="emit('update:currentPage', $event)"
        />
      </footer>
    </div>

    <DealImportDialog ref="importDialogRef" @import="onImport" />
    <DealExportDialog ref="exportDialogRef" @export="onExport" />
    <DataTransferImportDialog
      ref="dataTransferDialogRef"
      @done="emit('refresh')"
    />
  </section>
</template>
